"use client";
import React, { useState, useMemo, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import Image from "next/image";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import StartSIPStrip from "../strips/StartSIPStrip";
import { allSwpCalculation } from "@/utils/allCalculators";
import { useLoginModal } from "@/utils/useLoginModal";
import { ROOT_URL } from "@/utils/gloableVariables";

type SWPMonthlyData = {
  tenure: number;
  balance: string;
  currValue: number;
  totalInterestEarned: string;
  totalWithdrawn: string;
  interest: string;
  withdrawalAmount: string;
};

type SWPResult = {
  totalInterestEarned: number;
  totalWithdrawn: number;
  remainingInvestment: number;
  finalValue: number;
  investmentAmount: number;
  reportData: SWPMonthlyData[];
};

export default function SwpCalc() {
  const [isOpen, setIsOpen] = useState(false);

  const [initialAmount, setInitailAmount] = useState(10000000);
  const [withDrawType, setWithDrawType] = useState<"Monthly" | "Annually">("Monthly");
  const [withdrawAmount, setWithdrawAmount] = useState(10000);
  const [years, setYears] = useState(5);
  const [rate, setRate] = useState(8);

  const reportRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const isTab = useIsMobile(1025);
  const isMob = useIsMobile(769);

  const handleLogin = () => {
    open();
    setModalKey("swp-calculator");
  };

  const calculation: SWPResult = useMemo(() => {
    return allSwpCalculation(initialAmount, withdrawAmount, years, rate, withDrawType);
  }, [initialAmount, withdrawAmount, years, rate, withDrawType]);

  const handleExcelDownload = () => {
    if (!isLoggedIn) return handleLogin();
    const data =
      calculation?.reportData.map((row) => ({
        tenure: row.tenure,
        currValue: Number(row.currValue).toFixed(2),
        totalWithdrawn: Number(row.totalWithdrawn).toFixed(2),
        totalInterestEarned: Number(row.totalInterestEarned).toFixed(2),
        balance: Number(row.balance).toFixed(2),
        interest: Number(row.interest).toFixed(2),
        withdrawalAmount: Number(row.withdrawalAmount).toFixed(2),
      })) ?? [];
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SWP Report");
    XLSX.writeFile(workbook, "Swp_Investment_Report.xlsx");
  };

  const handlePDFDownload = () => {
    if (!isLoggedIn) return handleLogin();
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "p" });
    const { pageSize } = pdf.internal;
    const pageWidth = pageSize.getWidth();
    const pageHeight = pageSize.getHeight();
    // Layout
    const layout = {
      marginX: 12,
      headerY: 12,
      titleY: 18,
      headerArea: 22,
      footer: 12,
      rowH: 10,
    };
    const usableWidth = pageWidth - layout.marginX * 2;
    // Headers + column widths
    const headers = [
      withDrawType === "Monthly" ? "Months" : "Years",
      "Open Balance",
      "Withdrawal",
      "Interest On Balance",
      "Final Value",
    ];
    const colWidths = [0.14, 0.22, 0.18, 0.24, 0.22].map((p) => p * usableWidth);
    // Helpers
    const centerText = (text: string, x: number, y: number, opts = {}) =>
      pdf.text(text, x, y, { align: "center", baseline: "middle", ...opts });
    const drawHeader = (first = false) => {
      const y = layout.headerY;
      pdf.setFontSize(22).setTextColor(202, 202, 202).text("MySIPonline", layout.marginX, y);
      if (first) {
        const title = "SWP Report";
        pdf
          .setFontSize(18)
          .setTextColor(0)
          .text(title, pageWidth / 2, y, { align: "center" });
      }
    };
    const drawTableHeader = (y: number) => {
      let x = layout.marginX;
      headers.forEach((h, i) => {
        pdf.setFontSize(11).setTextColor(255, 255, 255).setFillColor(16, 188, 157).setDrawColor(217, 217, 217);
        pdf.rect(x, y, colWidths[i], layout.rowH, "FD"); // Fill + Border
        centerText(h, x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };
    const drawRow = (y: number, values: (string | number)[]) => {
      let x = layout.marginX;
      pdf.setFontSize(10).setTextColor(99, 89, 90).setDrawColor(217, 217, 217);
      values.forEach((val, i) => {
        pdf.rect(x, y, colWidths[i], layout.rowH);
        centerText(String(val), x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };
    const drawFooter = (pages: number) => {
      for (let p = 1; p <= pages; p++) {
        pdf.setPage(p);
        const footerY = pageHeight - 8;
        pdf.setFontSize(12).setTextColor(0).text(`Page ${p}/${pages}`, layout.marginX, footerY);
        const prefix = "Report by ";
        const w = pdf.getTextWidth(prefix);
        pdf.text(prefix, pageWidth - layout.marginX - w - 40, footerY);
        pdf
          .setTextColor(0, 102, 153)
          .textWithLink("MySIPonline", pageWidth - layout.marginX - 38, footerY, { url: "https://mysiponline.com" });
      }
    };
    // === Build PDF ===
    let y = layout.headerArea;
    drawHeader(true);
    drawTableHeader(y);
    y += layout.rowH;
    for (const row of calculation?.reportData ?? []) {
      const values = [
        row.tenure,
        Number(row.currValue).toFixed(2),
        Number(row.withdrawalAmount).toFixed(2),
        Number(row.interest).toFixed(2),
        Number(row.balance).toFixed(2),
      ];
      if (y + layout.rowH > pageHeight - layout.footer) {
        pdf.addPage();
        drawHeader();
        y = layout.headerArea + 4;
        drawTableHeader(y);
        y += layout.rowH;
      }
      drawRow(y, values);
      y += layout.rowH;
    }
    drawFooter(pdf.internal.pages.length - 1);
    pdf.save("Swp_Report.pdf");
  };

  const handleScrollReport = () => {
    if (reportRef.current) {
      const headerOffset = 30;
      const elementPosition = reportRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleRightPart = () => {
    if (!isTab) return;
    setIsOpen(true);
  };

  const displayedData = useMemo(() => {
    if (isLoggedIn) return calculation?.reportData ?? [];
    const realRows = (calculation?.reportData ?? []).slice(0, 2);
    const fakeCount = Math.max(5 - realRows.length, 0);
    const startYear = realRows.length > 0 ? realRows.length + 1 : 1;
    const fakeRows = Array.from({ length: fakeCount }, (_, i) => ({
      tenure: `${startYear + i}`,
      currValue: initialAmount,
      totalWithdrawn: withdrawAmount,
      totalInterestEarned: withdrawAmount / 25,
      withdrawalAmount: withdrawAmount / 25,
      interest: withdrawAmount / 25,
      balance: 858,
      isFake: true,
    }));
    return [...realRows, ...fakeRows];
  }, [isLoggedIn, calculation?.reportData]);

  return (
    <>
      {/* Hero Heading */}
      <section className="section px-3 sm:px-6 lg:px-3 mb-12 sm:mb-100">
        {/* Floating Calculator */}
        <div className="w-full relative text-white xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden xs:p-6">
          <div className="flex flex-col lg:flex-row gap-3.5 lg:gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg xs:rounded-none xs:shadow-none p-4 sm:p-0 lg:h-113.5">
            {/* Inputs */}
            <div className="w-full lg:w-[47%] 2lg:w-[41%] shrink-0">
              <div className="space-y-8">
                <CalcInputValues
                  label="Initial Investment"
                  min={5000}
                  max={500000000}
                  unit="₹"
                  amountValue={initialAmount}
                  unitPosition="left"
                  onChange={setInitailAmount}
                  showFloatingValue
                />

                <CalcInputValues
                  label="Withdraw per Month"
                  min={100}
                  max={1000000}
                  unit="₹"
                  amountValue={withdrawAmount}
                  modeOptions={["Monthly", "Annually"]}
                  defaultMode="Monthly"
                  onModeChange={(mode) => setWithDrawType(mode as "Monthly" | "Annually")}
                  showFloatingValue
                  unitPosition="left"
                  onChange={setWithdrawAmount}
                />

                <CalcInputValues
                  label="Over a period of (Years)"
                  min={1}
                  max={50}
                  unit="Years"
                  amountValue={years}
                  onChange={setYears}
                />
                <CalcInputValues
                  label="Expected Rate of Return(% P.a.)"
                  min={1}
                  max={30}
                  unit="%"
                  amountValue={rate}
                  onChange={setRate}
                  allowDecimal
                />
              </div>

              <button
                onClick={handleRightPart}
                className="block lg:hidden mt-4 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5"
              >
                Calculate
              </button>
            </div>

            {!isTab && <RightPart className="hidden lg:block" calculation={calculation!} />}
          </div>
          {!isMob && <StartSIPStrip className="hidden sm:flex" />}
        </div>
      </section>

      <section className="section mb-half-px sm:hidden">{isMob && <StartSIPStrip className="sm:hidden" />}</section>

      {/* Detailed Report (margin auto from calc height) */}
      <section ref={reportRef} className="section mb-half-px sm:mb-100">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-medium">Detailed Report</h2>

          <span className="ml-auto text-sm sm:text-base text-[#004b6b]">Download</span>

          <button
            onClick={handleExcelDownload}
            aria-label="Excel File Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src="https://www.mysiponline.com/images/swp-calculator/excel.svg"
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>

          <button
            onClick={handlePDFDownload}
            aria-label="PDF Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src="https://www.mysiponline.com/images/swp-calculator/pdf.svg"
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <div
            className={`${isLoggedIn ? "overflow-auto" : "overflow-hidden"} relative rounded-2xl sm:px-5 max-h-141 scrollbar-xs sm:scrollbar-md border-2 border-primaryBlack`}
          >
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-none">
                  <th className="p-0 sticky left-0 ">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      {withDrawType === "Monthly" ? "Months" : "Years"}
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Open Balance
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Interest On Balance
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Withdrawal
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Final Value
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {displayedData.map((row) => (
                  <tr key={row.tenure} className="border-b border-[#cacaca] border-dashed last:border-0">
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack sticky left-0 bg-[#f2f2f2] sm:bg-white">
                      {row.tenure}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.currValue).toFixed(0), {
                        prefix: "₹",
                      })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.interest).toFixed(2), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.withdrawalAmount).toFixed(0), { prefix: "₹" })}
                    </td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.balance).toFixed(0), {
                        prefix: "₹",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isLoggedIn && (
              <div className="absolute inset-0 top-20 bottom-1 xs:bottom-0 xs:top-24 z-1 mx-auto max-w-[98%] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.39)_41.78%)] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-6">
                <div className="gradient-blur rounded-2xl">
                  <div /> <div /> <div /> <div /> <div /> <div />
                </div>

                <div className="relative z-1 bg-white xs:w-4/5 xl:w-2/3 h-fit border border-[#e6e6e6] shadow-[0px_4px_4px_0px_#00000026] flex flex-col sm:flex-row items-center p-4 xxxs:p-5 md:pl-10 rounded-md overflow-hidden gap-2 sm:gap-0">
                  <Image
                    className="absolute z-0 top-0 left-0 bottom-0"
                    src="https://www.mysiponline.com/images/swp-calculator/curves.svg"
                    alt="curves-image"
                    width={60}
                    height={50}
                  />
                  <p className="flex items-center gap-2 mx-auto xs:mx-0">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="relative z-1 w-4 h-4 xs:w-7 xs:h-7"
                    >
                      <rect width="28" height="28" rx="14" fill="#1A51AA" />
                      <path
                        d="M14.3334 11.3334C13.6258 11.3334 12.952 11.474 12.3334 11.7246V9.3334C12.3334 8.22918 13.2292 7.3334 14.3334 7.3334C15.4382 7.3334 16.3334 8.22918 16.3334 9.3334V10H17.6668V9.3334C17.6668 7.49219 16.1746 6 14.3334 6C12.4922 6 11 7.49219 11 9.3334V12.5072C9.78199 13.4844 9 14.983 9 16.6666C9 19.612 11.388 22 14.3334 22C17.2788 22 19.6668 19.612 19.6668 16.6666C19.6668 13.7212 17.2788 11.3334 14.3334 11.3334ZM15 17.1484V18.6666H13.6668V17.1484C13.2696 16.918 13 16.4922 13 16C13 15.2642 13.597 14.6666 14.3334 14.6666C15.0698 14.6666 15.6668 15.2642 15.6668 16C15.6668 16.4922 15.3972 16.918 15 17.1484Z"
                        fill="white"
                      />
                    </svg>

                    <span className="whitespace-nowrap relative z-1 text-sm xs:text-base sm:text-lg text-primaryBlue font-medium">
                      Sign in to Unlock Premium Details
                    </span>
                  </p>
                  <button
                    onClick={handleLogin}
                    className="bg-transparent border-2 text-2xs sm:text-base border-primaryBlue text-primaryBlue rounded-full font-medium mx-auto sm:mr-0 sm:ml-auto py-1 px-10 w-fit transition duration-300 hover:bg-[#CAE7FF]"
                  >
                    Unlock Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {isTab && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          maxSize="max-w-[80%]"
          useDragToClose
          isBlurOverlay
          className="bg-white rounded-b-none sm:rounded-4xl"
        >
          <RightPart
            className="block lg:hidden"
            isOpen={isOpen}
            calculation={calculation!}
            onClose={() => setIsOpen(false)}
            onScrollToReport={handleScrollReport}
          />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({
  className,
  isOpen,
  calculation,
  onClose,
  onScrollToReport,
}: {
  isOpen?: boolean;
  className?: string;
  calculation: SWPResult;
  onClose?: () => void;
  onScrollToReport?: () => void;
}) {
  const [seriesVisibility, setSeriesVisibility] = useState<Record<string, boolean>>({
    "Total Withdrawal": true,
    "Remaining Value": true,
  });

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const legendRef = useRef<HTMLDivElement>(null);

  const investmentAmount = calculation?.investmentAmount ?? 0;
  const totalWithdrawn = calculation?.totalWithdrawn ?? 0;
  const finalValue = calculation?.finalValue ?? 0;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "bar",
      backgroundColor: "transparent",
      height: 180,
      style: {
        fontFamily: "inter, sans-serif",
      },
    },
    title: { text: "" },
    xAxis: {
      categories: ["Final Value"],
      labels: {
        style: {
          color: "#202020",
          fontSize: "16px",
          fontWeight: "600",
        },
      },
    },
    yAxis: { visible: false },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
          style: {
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "600",
            stroke: "#ffffff",
          },
        },
      },
      bar: {
        borderRadius: 16,
        pointPadding: 0.28,
        groupPadding: 0.05,
      },
    },
    series: [
      {
        name: "Total Withdrawal",
        data: [totalWithdrawn],
        color: "#498FDE",
        type: "bar",
        visible: seriesVisibility["Total Withdrawal"],
      },
      {
        name: "Remaining Value",
        data: [finalValue],
        color: "#d69a2d",
        type: "bar",
        visible: seriesVisibility["Remaining Value"],
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            chart: {
              height: 120,
            },
            xAxis: {
              labels: {
                align: "left",
                x: 10,
                y: -40,
                style: {
                  fontSize: "15px",
                },
              },
            },
          },
        },
      ],
    },
  };

  useEffect(() => {
    const chart = chartRef.current?.chart;
    const legendContainer = legendRef.current;
    if (!chart || !legendContainer) return;

    legendContainer.innerHTML = "";

    if (!calculation) return;

    const profitItem = document.createElement("div");
    profitItem.className = "interest_value text-sm sm:text-base font-bold mb-2 text-center";

    if (calculation.totalInterestEarned + calculation.investmentAmount - totalWithdrawn > 0) {
      profitItem.innerHTML = `
        You will earn a total profit of 
        <span class="text-primaryGreen font-semibold">
          ${formatIndianNumber(calculation.totalInterestEarned, {
            prefix: "₹",
          })}
        </span> on your SWP
      `;
    } else {
      profitItem.innerHTML = `
        <div class="text-sm sm:text-base text-[#ff0000] font-semibold">
          *Your investible amount is insufficient to meet your desired withdrawal amount.
        </div>`;
    }
    legendContainer.appendChild(profitItem);

    // Initial Investment
    const investItem = document.createElement("div");
    investItem.className = "flex justify-between text-[#222222]";
    investItem.innerHTML = `
      <span class="text-2sm sm:text-lg flex items-center gap-2 font-medium">
        <span class="inline-block w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full bg-black"></span>
        Initial Investment
      </span>
      <span class="text-2sm sm:text-lg font-medium">
        ${formatIndianNumber(investmentAmount, { prefix: "₹" })} 
        <span class="text-[#808080] text-2xs sm:text-2sm font-medium">
          (${formatCompactNumber(investmentAmount)})
        </span>
      </span>`;
    legendContainer.appendChild(investItem);

    // Series legend (Withdrawn + Remaining)
    chart.series.forEach((series) => {
      const item = document.createElement("div");
      const color = series.data?.[0]?.color || series.color || "#000";

      item.className = `
        text-2sm sm:text-lg flex justify-between text-[#222222]
        ${!seriesVisibility[series.name] ? "opacity-50" : ""}
      `;
      item.innerHTML = `
        <span class="text-2sm sm:text-lg flex items-center gap-2 font-medium">
          <span class="inline-block w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full" style="background:${color}"></span>
          ${series.name}
        </span>
        <span class="text-2sm sm:text-lg font-medium">
          ${formatIndianNumber(series.data?.[0]?.y ?? 0, { prefix: "₹" })}
          <span class="text-[#808080] text-2xs sm:text-2sm font-medium">
            (${formatCompactNumber(series.data?.[0]?.y ?? 0)})
          </span>
        </span>
      `;

      item.onclick = () => {
        setSeriesVisibility((prev) => ({
          ...prev,
          [series.name]: !prev[series.name],
        }));
      };

      legendContainer.appendChild(item);
    });
  }, [calculation, investmentAmount, totalWithdrawn, finalValue, seriesVisibility]);

  return (
    <>
      <div
        className={`w-full lg:w-[52%] 2lg:w-[57%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}
      >
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />

        <div
          ref={legendRef}
          className="bg-transparent sm:bg-white px-0 sm:p-4 rounded-2xl mt-4 mx-auto sm:w-[90%] space-y-4 sm:space-y-6"
        />
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 flex items-center rounded-b-2xl justify-around gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a href={`${ROOT_URL}start-sip.php`} className="w-full max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-6 xs:px-14 py-2 xs:py-2.5">
            Start SWP
          </a>
          <button
            onClick={() => {
              onClose?.();
              setTimeout(() => onScrollToReport?.(), 200);
            }}
            className="w-full max-w-fit mx-auto bg-white text-primaryGreen rounded-xl text-sm font-medium border border-primaryGreen px-5 xs:px-14 py-2 xs:py-2.5"
          >
            View Detail Report
          </button>
        </div>
      )}
    </>
  );
}
